// The one import this module allows itself: the purchase-restriction copy table. It is a plain
// data module with no React dependency, so it does not break the "pure module" property below —
// and inlining the words here instead would create a THIRD copy of text that already has to be
// kept in sync between the backend and src/lib/purchaseRestrictionCopy.js.
import { describeTerm } from './purchaseRestrictionCopy';
import { isUnderwritingPending } from './underwritingStatus';

// Pure derivation: turn a lead doc into a chronological list of events.
// One event = one thing that happened to the lead within the time window.
// Used by Home's New Deals tab to render an activity feed instead of
// aggregate counters.
//
// Sources of truth on a lead doc:
//   - data.dedupHits[]      — every inbound copy (including exact duplicates).
//                             Populated for all leads ingested after the
//                             dedupHits schema landed.
//   - data.createdAt        — fallback "new" event for legacy leads that
//                             predate dedupHits.
//   - data.archiveRestoredAt — legacy field from the old auto-restore behavior
//                             (removed). No longer written; older leads may
//                             still carry it. Read kept so historical events
//                             surface for the rail's 24h window.

const EVENT_TYPES = {
    NEW: 'new',
    PRICE_CHANGE: 'price_change',
    RE_BLAST: 're_blast',
    DUPLICATE: 'duplicate',
    RE_SURFACED: 're_surfaced'
};

function toMs(ts) {
    if (!ts) return 0;
    if (ts._seconds !== undefined) return ts._seconds * 1000 + (ts._nanoseconds || 0) / 1e6;
    if (ts.seconds !== undefined) return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
    if (typeof ts.toDate === 'function') return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === 'number') return ts;
    if (typeof ts === 'string') {
        const parsed = Date.parse(ts);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
}

function senderKeyOf(hit) {
    return (hit?.senderEmail || '').toLowerCase().trim()
        || (hit?.senderPhone || '').replace(/\D/g, '')
        || (hit?.senderName || '').toLowerCase().trim()
        || null;
}

/**
 * Derive all events on a lead that fall within [cutoffMs, now].
 * Returns an array of event objects:
 *   { type, timestamp, leadId, propertyId, lead, asking, priorAsking, sender }
 */
export function deriveLeadEvents(lead, cutoffMs) {
    const data = lead?.data;
    if (!data) return [];

    const events = [];
    const hits = Array.isArray(data.dedupHits) ? [...data.dedupHits] : [];
    hits.sort((a, b) => toMs(a?.timestamp) - toMs(b?.timestamp));

    if (hits.length > 0) {
        for (let i = 0; i < hits.length; i++) {
            const hit = hits[i];
            const ms = toMs(hit?.timestamp);
            const { type, senderOrdinal } = classifyHit(hits, i);
            if (ms < cutoffMs) continue;
            events.push({
                type,
                senderOrdinal: senderOrdinal ?? null,
                timestamp: ms,
                leadId: lead.leadId,
                propertyId: lead.propertyId || data.propertyId,
                lead,
                asking: hit?.asking ?? null,
                priorAsking: i > 0 ? (hits[i - 1]?.asking ?? null) : null,
                sender: {
                    email: hit?.senderEmail || null,
                    phone: hit?.senderPhone || null,
                    name: hit?.senderName || null
                },
                source: hit?.source || null
            });
        }
    } else {
        // Legacy lead with no dedupHits — emit a single "new" event at
        // creation time if it's still inside the window. Avoids silent
        // dropouts on pre-schema docs.
        const createdMs = toMs(data.createdAt);
        if (createdMs >= cutoffMs && createdMs > 0) {
            events.push({
                type: EVENT_TYPES.NEW,
                timestamp: createdMs,
                leadId: lead.leadId,
                propertyId: lead.propertyId || data.propertyId,
                lead,
                asking: data.currentAskingPrice ?? null,
                priorAsking: null,
                sender: data.currentSender || data.originalSender || null,
                source: data.source || data.originSource || null
            });
        }
    }

    const restoredMs = toMs(data.archiveRestoredAt);
    if (restoredMs >= cutoffMs && restoredMs > 0) {
        events.push({
            type: EVENT_TYPES.RE_SURFACED,
            timestamp: restoredMs,
            leadId: lead.leadId,
            propertyId: lead.propertyId || data.propertyId,
            lead,
            asking: data.currentAskingPrice ?? null,
            priorAsking: null,
            sender: data.currentSender || null,
            source: data.source || data.originSource || null
        });
    }

    return events;
}

// Classify a dedupHit by index. Index 0 is always "new"; subsequent hits
// compare to all priors to decide between price_change / re_blast / duplicate.
// For re_blast, also returns senderOrdinal — 1-indexed position of this sender
// among unique senders seen on the lead (1st = original NEW, 2nd = first
// re-blast, etc.). Used by the rail to render "2nd sender", "3rd sender", …
function classifyHit(hits, i) {
    if (i === 0) return { type: EVENT_TYPES.NEW };
    const hit = hits[i];
    const prior = hits[i - 1];
    const priorPrice = prior?.asking ?? null;
    const thisPrice = hit?.asking ?? null;
    if (thisPrice != null && priorPrice != null && thisPrice !== priorPrice) {
        return { type: EVENT_TYPES.PRICE_CHANGE };
    }
    const senderKeysSeen = new Set();
    for (let j = 0; j < i; j++) {
        const k = senderKeyOf(hits[j]);
        if (k) senderKeysSeen.add(k);
    }
    const senderKey = senderKeyOf(hit);
    if (senderKey && !senderKeysSeen.has(senderKey)) {
        return {
            type: EVENT_TYPES.RE_BLAST,
            senderOrdinal: senderKeysSeen.size + 1
        };
    }
    return { type: EVENT_TYPES.DUPLICATE };
}

// 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th", 22 → "22nd", …
export function ordinalSuffix(n) {
    const v = Math.abs(n) % 100;
    if (v >= 11 && v <= 13) return 'th';
    switch (Math.abs(n) % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
}

export const LEAD_EVENT_TYPES = EVENT_TYPES;

// Display config per type — chip emoji, label, color class. Single source of
// truth so summary chips + feed rows stay in sync if labels change.
export const LEAD_EVENT_CONFIG = {
    [EVENT_TYPES.NEW]:          { emoji: '🆕', label: 'New',          chipClass: 'bg-colour-jade-01' },
    [EVENT_TYPES.PRICE_CHANGE]: { emoji: '💵', label: 'Price change', chipClass: 'bg-colour-blue-01' },
    [EVENT_TYPES.RE_BLAST]:     { emoji: '📦', label: 'New sender',   chipClass: 'bg-colour-magenta-01' },
    [EVENT_TYPES.DUPLICATE]:    { emoji: '🔁', label: 'Duplicate',    chipClass: 'bg-colour-brown-01' },
    [EVENT_TYPES.RE_SURFACED]:  { emoji: '🗃️', label: 'Un-archived',  chipClass: 'bg-colour-green-01' }
};

// ---------------------------------------------------------------------------
// Lead disposition — "how was this lead processed?"
//
// One chip per event. Picks the most informative signal from a priority
// ordering: pipeline status > non-new ingest event > archive state >
// triage tier. Intake-event types (duplicate, re-blast, etc.) win over
// triage tier because the ingest action is the actionable info for that
// row — the underlying lead's tier is secondary context.
// ---------------------------------------------------------------------------
export const DISPOSITIONS = {
    HOT:          'hot',          // LOOK_NOW with real ARV+profit — the one chip worth attention
    PROCESSED:    'processed',    // anything that completed normally but isn't hot
    ARCHIVED:     'archived',     // lead currently dismissed (isArchived === true)
    PROCESSING:   'processing',   // pipeline still running (queued/processing)
    DISCARDED:    'discarded',    // failed, OR completed but unusable (no address)
    DUPLICATE:    'duplicate',    // silent dedup hit
    RE_BLAST:     're_blast',     // different sender
    RE_SURFACED:  're_surfaced',  // legacy: archived → auto-restored (no longer fires)
    PRICE_CHANGE: 'price_change', // entry with different price
    FILTERED:     'filtered'      // dropped during ingestion (data.filtered === true) — never enriched, no credit spent
};

// Each disposition gets a distinct -01 background so the rail's chip strip
// can be scanned for "why isn't this lead in my table" answers at a glance.
// PROCESSED stays in SILENT_DISPOSITIONS — no chip rendered for normal
// arrivals, so the eye lands on the anomalies.
export const DISPOSITION_CONFIG = {
    [DISPOSITIONS.HOT]:          { emoji: '🔥', label: 'Hot',          chipClass: 'bg-colour-jade-01' },
    [DISPOSITIONS.PROCESSED]:    { emoji: '',   label: 'Processed',    chipClass: 'bg-colour-light' },
    [DISPOSITIONS.ARCHIVED]:     { emoji: '',   label: 'Archived',     chipClass: 'bg-colour-yellow-01' },
    [DISPOSITIONS.PROCESSING]:   { emoji: '',   label: 'Processing',   chipClass: 'bg-colour-aqua-01' },
    [DISPOSITIONS.DISCARDED]:    { emoji: '',   label: 'Discarded',    chipClass: 'bg-colour-red-01' },
    [DISPOSITIONS.DUPLICATE]:    { emoji: '',   label: 'Duplicate',    chipClass: 'bg-colour-brown-01' },
    [DISPOSITIONS.RE_BLAST]:     { emoji: '',   label: 'New sender',   chipClass: 'bg-colour-magenta-01' },
    [DISPOSITIONS.RE_SURFACED]:  { emoji: '',   label: 'Un-archived',  chipClass: 'bg-colour-green-01' },
    [DISPOSITIONS.PRICE_CHANGE]: { emoji: '',   label: 'Price change', chipClass: 'bg-colour-blue-01' },
    // Muted by design — filtered rows are secondary to real arrivals. The chip
    // carries the "why" (rendered as the row label), so it's NOT silent.
    [DISPOSITIONS.FILTERED]:     { emoji: '',   label: 'Filtered',     chipClass: 'bg-colour-light' }
};

// Friendly headline text for a lead's `data.filterReasons` codes (written by
// the ingestion pipeline — see functions/propertyIngestionV2 and
// functions/flows/unipile/unipileEmailWorker.js). Unknown codes pass through
// verbatim so a new backend reason never renders blank.
//
// These are the SHORT chip-style labels used in row summaries; the long-form
// per-reason explanation (with observed values + buy-box thresholds) lives in
// describeFilterReasons below.
const FILTER_REASON_LABELS = {
    mls_excluded:       'MLS listing',
    extraction_failed:  "Couldn't read email",
    no_address:         'No address found',
    deal_no_address:    'No street address',
    geography:          'Outside buy-box area',
    // ✏️ Added 2026-09-04 alongside the gate that writes it (see the co_living_fit
    // note below for why that pairing is non-negotiable). NOT a synonym for
    // `geography`: this one means we could not place the property on the map at
    // all, so we never established it was outside anything.
    geography_unknown:  "Couldn't locate this property",
    price:              'Price outside buy box',
    propertyType:       'Wrong property type',
    characteristics:    'Beds/baths/size off',
    unidentified:       'Not a confirmed home',
    address_unverified: 'Missing street number',
    insufficient_credits: 'Not enough credits',
    condition:          'Too much rehab needed',
    // ✏️ Added 2026-08-10. These two are written by the CO-LIVING gates
    // (functions/propertyIngestionV2/underwriting/processor.js), not the buy-box
    // filter, and neither had a label — so the rail rendered the raw code
    // `co_living_fit` verbatim (see the "unknown codes pass through" note above).
    // On the 2026-08-10 Atlanta book that was 13 of 14 archives showing a bare
    // internal key. `co_living_route` is worded as the house being too CLEAN,
    // because "wrong route" reads like a config error rather than the deliberate
    // filter it is.
    co_living_route:    'Too good condition for hard money',
    co_living_fit:      "Doesn't fit co-living",
    // ✏️ Added 2026-09-09. The gate (maybeFilterComingSoon in
    // functions/propertyIngestionV2/underwriting/processor.js) shipped without a label, so the
    // rail rendered the raw code `coming_soon` verbatim — the same regression `co_living_fit`
    // had. This one runs for EVERY company, not just co-living, so it is not a rare row.
    coming_soon:        'Coming soon',
    // ✏️ Added 2026-08-13 with the purchase-restriction gate. Registered at the same time as the
    // gate that writes it, deliberately — `co_living_fit` shipped without a label and rendered its
    // raw internal key on 13 of 14 archives for weeks (see the note above).
    purchase_blocked:   "Investors can't buy this one",
    // ✏️ Added with registerUncardedLeads.js's target-geography archive (functions/followUp/uncardedLeads.js).
    // Same regression class as the others in this file: without a label the rail renders the raw code
    // `outside_target_geography` verbatim. NOT a synonym for `geography`: this lead was fully underwritten and
    // buyer-matched, and was archived only because it sits outside the areas outreach currently calls.
    outside_target_geography: 'Outside current calling zones'
};

// Rehab levels 0–5, worst-condition scale (matches REHAB_LEVELS_TEMPLATE in
// src/defaults and the backend condition filter). Indexed by level number.
const REHAB_LEVEL_NAMES = ['Pristine', 'Lipstick', 'Cosmetic Update', 'Moderate Overhaul', 'Full Renovation', 'Complete Rebuild'];

// ── Did this filtered lead actually cost credits? ────────────────────────────
//
// 🚨 THE REASON CODE CANNOT ANSWER THIS — don't be tempted to map one to the
// other. The charge point is a single line in
// functions/propertyIngestionV2/underwriting/processor.js (search "BILLING:
// charge BEFORE any paid analysis runs"). Gates above it are free; gates below it
// archive leads we had already paid to analyse. That alone would make a reason→cost
// table possible, but two things break it:
//   - ONE REASON LANDS ON BOTH SIDES. `co_living_fit` normally fires below the
//     charge, but a co-living-only company whose pre-analysis gates already killed
//     co-living takes the `coLivingOnlyDeadLead` branch — charged nothing, then
//     archived on that very same reason. Both shapes are live in the data.
//   - `insufficient_credits` is not reliably free either: the archive fires when
//     THIS run couldn't pay, which says nothing about what an earlier run already
//     charged and is still holding.
// So we read what the ledger says instead of inferring it from the reason.
//
// THREE STATES, NOT TWO. "Not charged" hides two different events: never charged,
// and charged-then-refunded because the analysis ran but produced nothing usable.
// Both end at a zero balance, but "we never spent anything on it" and "we analysed
// it and gave the credit back" are not the same story to read off a row — and the
// refunded shape is common, not a curiosity, so collapsing them mislabels a large
// slice of the unpaid rows. A PARTIAL refund (charged arv+coLiving, refunded
// coLiving) still holds a charge and stays `charged` at the lower count, because
// the operator was net billed.
//
// To re-measure any of this rather than trusting a number in a comment: read
// `data.creditChargedFor` / `data.creditRefundedAt` across a company's
// `data.filtered === true` leads, grouped by `data.filterStage`.
//
// `data.creditChargedFor` is the authoritative per-analysis map written by
// functions/utils/chargeLeadCredit.js, and refundLeadCredit DELETES the analysis
// from it, so what remains is what the operator is actually out of pocket for.
// That same function returns early on a not-currently-charged analysis without
// writing, so `creditRefundedAt` can only exist on a lead that really was charged.
// Mirrors chargedMapOf(), legacy boolean seeding included.
const CREDIT_ANALYSES = ['arv', 'coLiving', 'repair'];
const CREDIT_ANALYSIS_LABELS = { arv: 'ARV', coLiving: 'co-living', repair: 'repair estimate' };

/**
 * @param {object} leadData - a lead's `data` blob.
 * @returns {{ state: 'charged'|'refunded'|'free', charged: boolean, credits: number,
 *             analyses: string[], label: string }}
 *   credits — credits the lead still holds (0, 1 or 2); what the operator paid.
 *   label   — tooltip-ready sentence.
 */
export function resolveLeadCreditCharge(leadData) {
    const d = leadData || {};
    const map = { ...(d.creditChargedFor || {}) };
    // Legacy pre-migration leads carry a single boolean, which always meant ARV.
    if (d.creditCharged === true && map.arv === undefined) map.arv = true;
    const analyses = CREDIT_ANALYSES.filter((a) => map[a] === true);
    const credits = analyses.length;

    if (credits > 0) {
        const names = analyses.map((a) => CREDIT_ANALYSIS_LABELS[a] || a).join(' + ');
        return {
            state: 'charged',
            charged: true,
            credits,
            analyses,
            label: `Cost ${credits} credit${credits === 1 ? '' : 's'} — we'd already run the ${names} before this filter caught it.`
        };
    }
    // Nothing held. Distinguish "never spent" from "spent and given back".
    if (d.creditRefundedAt) {
        return {
            state: 'refunded',
            charged: false,
            credits: 0,
            analyses: [],
            label: "Cost nothing in the end — the analysis ran but couldn't produce a usable number, so the credit went back."
        };
    }
    // `creditWaived` lands here too, and belongs here: the legacy fail-open let the
    // work run without taking a credit, so the operator was never billed either.
    return {
        state: 'free',
        charged: false,
        credits: 0,
        analyses: [],
        label: "Never charged — filtered before we spent anything on it."
    };
}

// One-line collapsed summary for a list of reasons. Single-reason rows show
// that reason verbatim; multi-reason rows show the first reason plus a "+N
// more" so the row stays compact while the accordion carries the full detail.
export function filterReasonLabel(reasons) {
    const list = Array.isArray(reasons) ? reasons : (reasons ? [reasons] : []);
    const labels = list.map(r => FILTER_REASON_LABELS[r] || r).filter(Boolean);
    if (labels.length === 0) return 'Filtered out';
    if (labels.length === 1) return labels[0];
    return `${labels[0]} +${labels.length - 1} more`;
}

// True when a lead was archived ONLY because it sits outside the currently-worked calling zones
// (functions/followUp/uncardedLeads.js#archiveOutOfGeographyLeads) — a real, fully-underwritten, buyer-matched
// deal, unlike every other filter reason here, which means the deal itself was disqualified. Mirrors
// functions/utils/leadArchive.js#isZoneOnlyArchive; the frontend bundle can't import that backend module
// directly, so this copy is kept in sync by hand — including on a non-array filterReasons, where it must
// answer `false` exactly like the backend does (Array.isArray-only, no bare-string normalization), even
// though filterReasonLabel elsewhere in this file DOES normalize a bare string. The single writer
// (archiveFilteredLead) always writes a real array, so this never fires on a genuine archive; treating
// anything else as `false` is the direction that can never wrongly mark a lead "viewable".
export function isZoneOnlyArchive(data) {
    if (!data) return false;
    return data.filterStage === 'target_geography' && Array.isArray(data.filterReasons) && data.filterReasons.includes('outside_target_geography');
}

// Snake_case → display label for the property types the buy box understands.
// Mirrors BUYBOX_PROPERTY_TYPES in src/utils/buyBoxUtils.js but inlined here
// so this pure module has no React-tree dependency.
const PROPERTY_TYPE_LABEL_MAP = {
    single_family: 'Single Family',
    multi_family:  'Multi-Family',
    townhouse:     'Townhouse',
    condo:         'Condo',
    mobile:        'Mobile',
    land:          'Land'
};

// Canonicalize the strings backend's verifyPropertyType returns
// (functions/processXPosts/dataProcessing.js normalizePropertyType) to the
// SAME display labels the buy-box picker uses. Without this, an observed
// "Multi Family" (no hyphen, from the verifier) lands next to an allowed
// "Multi-Family" (with hyphen, from the buy-box snake_case map) in the same
// sentence and reads as inconsistent.
const PROPERTY_TYPE_DISPLAY_ALIASES = {
    'multi family': 'Multi-Family'
};

function humanizePropertyType(t) {
    if (!t || typeof t !== 'string') return null;
    const trimmed = t.trim();
    if (!trimmed) return null;
    // Snake_case key from the buy box → canonical label.
    if (PROPERTY_TYPE_LABEL_MAP[trimmed]) return PROPERTY_TYPE_LABEL_MAP[trimmed];
    // Already-humanized value from the verifier — alias to the canonical
    // display when the spelling differs.
    const aliased = PROPERTY_TYPE_DISPLAY_ALIASES[trimmed.toLowerCase()];
    if (aliased) return aliased;
    // Already humanized in a way we recognize (has uppercase or whitespace) —
    // pass through. Otherwise title-case the snake_case for unknown codes.
    if (/[A-Z]/.test(trimmed) || /\s/.test(trimmed)) return trimmed;
    return trimmed.split('_').filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
}

function fmtMoney(v) {
    if (v == null || Number.isNaN(Number(v))) return null;
    return `$${Number(v).toLocaleString()}`;
}

function fmtRange(min, max, fmt) {
    const hi = max != null ? fmt(max) : null;
    const lo = min != null ? fmt(min) : null;
    if (lo && hi) return `${lo}–${hi}`;
    if (lo) return `${lo}+`;
    if (hi) return `up to ${hi}`;
    return null;
}

function fmtList(values, max = 6) {
    if (!Array.isArray(values) || values.length === 0) return null;
    if (values.length <= max) return values.join(', ');
    return `${values.slice(0, max).join(', ')} +${values.length - max} more`;
}

/**
 * Turn a lead's filterReasons (+ optional filterDetails snapshot + lead data)
 * into a list of rich, English explanations ready to render in the activity
 * rail's filtered-row accordion.
 *
 * Each item is { code, title, body }. Title is short (chip-style); body uses
 * the snapshot's observed values + buy-box thresholds to spell out *why* —
 * e.g. "This is a Multi-Family; your buy box accepts Single Family, Condo."
 *
 * filterDetails is the geometry-free snapshot built by buildFilterDetails
 * (functions/propertyIngestionV2/buyBoxMatching/ingestionGate.js). leadData
 * is the lead's `data` object — used as a fallback for things like the city
 * name that aren't in the snapshot.
 */
export function describeFilterReasons(reasons, filterDetails, leadData) {
    const list = Array.isArray(reasons) ? reasons : (reasons ? [reasons] : []);
    if (list.length === 0) return [];
    const observed = filterDetails?.observed || {};
    const buyBox = filterDetails?.buyBox || {};
    const data = leadData || {};
    const address = data.address || {};

    return list.map((code) => {
        switch (code) {
            // ── The PURCHASE-RESTRICTION gate ─────────────────────────────────
            // Not the buy box and not co-living: a legal restriction on the property
            // itself. Only two things reach here — a deed/income restriction, or a
            // co-op board that bars investors — so the body can be specific rather
            // than hedged. Registered alongside the gate that writes it, so this can
            // never render a bare `purchase_blocked` the way `co_living_fit` did.
            case 'purchase_blocked': {
                const term = filterDetails?.purchaseBlocked?.term || null;
                const copy = term ? describeTerm(term) : null;
                return {
                    code,
                    title: copy?.title || FILTER_REASON_LABELS.purchase_blocked,
                    body: (copy?.body ? `${copy.body} ` : 'An investor cannot legally buy this one. ')
                        + 'This is written into the property, not your settings, so changing your '
                        + 'buy box will not bring back deals like it.'
                };
            }
            // ── The CO-LIVING gates ───────────────────────────────────────────
            // Neither of these is the buy-box filter, and both used to fall through
            // to the raw-code default below. Added 2026-08-10.
            case 'co_living_route': {
                // The house is too CLEAN: this strategy only buys homes needing enough
                // work to justify a bridge loan + refinance, so a straight-DSCR-able
                // house is excluded however good its numbers are. Deliberately says
                // nothing about cash-on-cash — on this path the max offer is never
                // even solved, so the returns figures are a by-product of the route
                // rather than the reason (coLivingCashOutRank.js 'not_bridge_path').
                return {
                    code,
                    title: 'Too good condition for hard money',
                    body: 'This home is in good enough shape to buy on a straight rental loan, '
                        + 'so it is not a hard-money-and-refinance deal. The repairs are what make '
                        + 'these deals work, so a home that does not need them is skipped — however '
                        + 'good its numbers look.'
                };
            }
            case 'co_living_fit': {
                // One bucket, several very different causes. Read the finer reason
                // rather than inventing one — the pre-analysis gates (pool / flood
                // zone / non-single-family / under contract) never ran the analysis
                // at all, so they must not be described as missing a bar.
                const why = filterDetails?.coLivingFit?.reason || null;
                const BODIES = {
                    pool: 'This home has a pool, which your settings exclude from co-living.',
                    flood_zone: 'This home is in a FEMA flood zone, which your settings exclude from co-living.',
                    property_type: 'Co-living only runs on single-family homes.',
                    under_contract: 'This home is already under contract, so the numbers were never run on it.',
                    parking: 'There is not enough parking here for a house full of tenants.',
                    hoa: 'The HOA rules out street parking for tenants.',
                    gated: 'This is a gated community, which rules out street parking for tenants.',
                    bedrooms: 'After conversion this home still has fewer bedrooms than you accept.',
                    rent: 'There is no co-living room-rate data for this market.',
                    no_data: "We couldn't work out a bedroom count for this home.",
                    parking_unresolved: 'Parking was never confirmed for this home.',
                    cash_on_cash: 'At the asking price the cash-on-cash return is below your bar.',
                    net_cash_flow: 'At the asking price the monthly cash flow is below your bar.',
                    vacant_land: 'This is vacant land, and it didn\'t pass the new-construction checklist (water/sewer, lot size, zoning, or price).',
                    multi_parcel_lot: 'This listing describes multiple separate lots bundled together — get the individual parcel numbers and per-lot pricing before treating this as one lot.',
                };
                return {
                    code,
                    title: FILTER_REASON_LABELS.co_living_fit,
                    body: BODIES[why] || 'This home did not meet your co-living criteria.'
                };
            }
            // ── The COMING-SOON gate ───────────────────────────────────────
            // Not the buy box and not a judgement on the house — it just isn't for sale yet, so
            // the body says "come back", not "this was rejected". Nothing in the operator's
            // settings turns this off, so don't point them at their buy box.
            case 'coming_soon':
                return {
                    code,
                    title: FILTER_REASON_LABELS.coming_soon,
                    body: "This listing isn't active yet — there are no showings and no offers until it "
                        + 'goes live, so we skipped the analysis. It comes back through if it lists.'
                };
            case 'condition': {
                const c = filterDetails?.condition || {};
                const lvl = Number.isInteger(c.level) ? c.level : null;
                const max = Number.isInteger(c.maxAllowed) ? c.maxAllowed : null;
                const lvlName = lvl != null ? (REHAB_LEVEL_NAMES[lvl] || `level ${lvl}`) : null;
                const maxName = max != null ? (REHAB_LEVEL_NAMES[max] || `level ${max}`) : null;
                return {
                    code,
                    title: 'Too much rehab needed',
                    body: (lvlName && maxName)
                        ? `This home needs a ${lvlName}-level rehab; you only accept ${maxName} condition or better.`
                        : 'This home needs more work than your condition filter allows.'
                };
            }
            case 'propertyType': {
                const observedType = humanizePropertyType(observed.propertyType) || 'unknown';
                const allowed = Array.isArray(buyBox.propertyTypes) && buyBox.propertyTypes.length
                    ? buyBox.propertyTypes.map(humanizePropertyType).filter(Boolean).join(', ')
                    : null;
                return {
                    code,
                    title: 'Wrong property type',
                    body: allowed
                        ? `This is a ${observedType}. Your buy box only accepts ${allowed}.`
                        : `This is a ${observedType}, which isn't in your buy box.`
                };
            }
            case 'price': {
                const priceStr = fmtMoney(observed.purchasePrice) || 'The asking price';
                const range = fmtRange(buyBox.priceRange?.min, buyBox.priceRange?.max, fmtMoney);
                return {
                    code,
                    title: 'Price outside your range',
                    body: range
                        ? `${priceStr} falls outside your buy box of ${range}.`
                        : `${priceStr} falls outside your buy box.`
                };
            }
            case 'geography': {
                const where = [address.city, address.state || observed.state, address.zipcode || observed.zipCode]
                    .filter(Boolean).join(', ');
                const geo = buyBox.geography || {};
                const targets = [];
                if (Array.isArray(geo.polygons) && geo.polygons.length) {
                    targets.push(`your areas (${fmtList(geo.polygons)})`);
                }
                if (Array.isArray(geo.counties) && geo.counties.length) {
                    targets.push(`counties ${fmtList(geo.counties)}`);
                }
                if (Array.isArray(geo.zipCodes) && geo.zipCodes.length) {
                    targets.push(`ZIPs ${fmtList(geo.zipCodes)}`);
                }
                if (Array.isArray(geo.states) && geo.states.length) {
                    targets.push(`states ${fmtList(geo.states)}`);
                }
                const target = targets.join(' · ') || null;
                const prefix = where ? `Property is in ${where}` : 'Property location';
                return {
                    code,
                    title: 'Outside your buy-box area',
                    body: target
                        ? `${prefix} — your buy box covers ${target}.`
                        : `${prefix} is outside your buy-box area.`
                };
            }
            // Deliberately NOT merged with 'geography' above. That case asserts the
            // property is outside the user's area; here we never managed to place it,
            // so the honest body says what we could not do and what would fix it —
            // an address good enough to look up. Claiming "outside your area" for
            // these is what sent a genuine in-zone Plano house to the archive
            // labelled out-of-area.
            case 'geography_unknown': {
                const where = [address.city, address.state || observed.state, address.zipcode || observed.zipCode]
                    .filter(Boolean).join(', ');
                const geo = buyBox.geography || {};
                const polygonOnly = Array.isArray(geo.polygons) && geo.polygons.length
                    && !(Array.isArray(geo.states) && geo.states.length)
                    && !(Array.isArray(geo.counties) && geo.counties.length)
                    && !(Array.isArray(geo.zipCodes) && geo.zipCodes.length);
                const tail = polygonOnly
                    ? 'Your buy box is drawn as map areas, which need exact coordinates to test against.'
                    : 'We had none of the location details your buy box filters on.';
                return {
                    code,
                    title: "Couldn't locate this property",
                    body: (where ? `We couldn't pin down exactly where this property is (we only had ${where}). ` : "We couldn't pin down where this property is. ")
                        + `${tail} It was set aside because we could not place it — not because we found it outside your area. `
                        + 'A full street address will usually resolve it.'
                };
            }
            // Deliberately NOT merged with 'geography' above. That case means the buy box rejected the property;
            // this one means the property PASSED underwriting and buyer matching and was archived only because
            // it sits outside the areas outreach currently calls (functions/followUp/uncardedLeads.js). Claiming
            // "outside your buy box" here would be the same false claim geography_unknown was added to stop.
            case 'outside_target_geography': {
                const metro = filterDetails?.metro ? ` (${filterDetails.metro})` : '';
                return {
                    code,
                    title: 'Outside current calling zones',
                    body: `This property was fully analysed and matched to buyers, then archived because it sits outside the areas outreach currently calls${metro} — not because it failed your buy box. Widen the calling zones to bring it back into rotation, or restore it directly if you want it worked regardless.`
                };
            }
            case 'characteristics': {
                const r = buyBox.propertyRequirements || {};
                const diffs = [];
                const check = (val, min, max, label, fmt = (v) => v) => {
                    if (val == null) return;
                    if (min != null && Number(val) < Number(min)) {
                        diffs.push(`${label} is ${fmt(val)} (need ${fmt(min)}+)`);
                    } else if (max != null && Number(val) > Number(max)) {
                        diffs.push(`${label} is ${fmt(val)} (max ${fmt(max)})`);
                    }
                };
                check(observed.bedrooms, r.minBedrooms, r.maxBedrooms, 'Beds');
                check(observed.bathrooms, r.minBathrooms, r.maxBathrooms, 'Baths');
                check(observed.squareFeet, r.minSquareFeet, r.maxSquareFeet, 'Sqft',
                    (v) => Number(v).toLocaleString());
                check(observed.yearBuilt, r.minYearBuilt, r.maxYearBuilt, 'Year built');
                return {
                    code,
                    title: "Doesn't fit your size/age requirements",
                    body: diffs.length
                        ? diffs.join(' · ')
                        : 'Property characteristics fall outside your buy box.'
                };
            }
            case 'unidentified': {
                const observedType = humanizePropertyType(observed.propertyType);
                const allowed = Array.isArray(buyBox.propertyTypes) && buyBox.propertyTypes.length
                    ? buyBox.propertyTypes.map(humanizePropertyType).filter(Boolean).join(', ')
                    : null;
                const typeClause = observedType
                    ? `its type came back as ${observedType}`
                    : "we couldn't confirm its property type";
                return {
                    code,
                    title: 'Not a confirmed home',
                    body: `We couldn't confirm this is a home you can underwrite — no bedroom or bathroom count resolved and ${typeClause}, which isn't a residential property.${allowed ? ` Your buy box accepts ${allowed}.` : ''}`
                };
            }
            case 'mls_excluded':
                return {
                    code,
                    title: 'MLS listing',
                    body: "Your ingestion gates exclude deals sourced from the MLS — we didn't process this one."
                };
            case 'extraction_failed':
                return {
                    code,
                    title: "Couldn't read the email",
                    body: "We couldn't extract a deal from the message contents — usually means the format wasn't recognized as a property."
                };
            case 'no_address':
                return {
                    code,
                    title: 'No address found',
                    body: "We couldn't find a property address anywhere in the message."
                };
            case 'deal_no_address':
                return {
                    code,
                    title: 'Deal had no street address',
                    body: "We identified this as a deal but couldn't extract a usable street address — common for teaser-style emails that hide the address until you reply."
                };
            case 'address_unverified':
                return {
                    code,
                    title: 'Address missing street number',
                    body: "The address parsed but is missing a house number, so we can't pin it to a parcel."
                };
            default:
                return {
                    code,
                    title: FILTER_REASON_LABELS[code] || code,
                    body: ''
                };
        }
    });
}

// Curated, customer-safe display text for a lead's `data.errorMessage`.
//
// IMPORTANT: errorMessage is NOT safe to render verbatim. The ingestion
// pipeline writes it from two sources (functions/propertyIngestionV2/
// enrichmentWorker.js): curated business-failure strings AND raw caught
// exceptions (`error.message`) / internal diagnostics ("COMP_FETCHER_V1_URL
// not set", "Cannot read properties of undefined…"). Rendering raw values
// would leak internals to customers.
//
// This map is fail-CLOSED: only exact, known messages get a friendly label.
// Anything unrecognized returns null so callers fall back to the generic
// disposition label — a new/raw backend message can never leak through.
const SAFE_ERROR_MESSAGE_LABELS = {
    'Could not identify valid address from input': 'No address found in the message',
    'Failed to extract structured data from input': "Couldn't read the deal from the message",
    'Lead has incomplete address - cannot underwrite': 'Address was incomplete',
    'Lead has incomplete address': 'Address was incomplete',
    'Could not determine county for property': "Couldn't locate the property's county"
};

export function safeErrorMessageLabel(errorMessage) {
    if (!errorMessage || typeof errorMessage !== 'string') return null;
    return SAFE_ERROR_MESSAGE_LABELS[errorMessage.trim()] || null;
}

// Display order for the summary chip strip + filter UI. PROCESSED is
// intentionally omitted — it's the "default / nothing notable happened"
// state, so labeling it everywhere would drown out the actual signals.
export const DISPOSITION_DISPLAY_ORDER = [
    DISPOSITIONS.HOT,
    DISPOSITIONS.ARCHIVED,
    DISPOSITIONS.PRICE_CHANGE,
    DISPOSITIONS.RE_BLAST,
    DISPOSITIONS.RE_SURFACED,
    DISPOSITIONS.DUPLICATE,
    DISPOSITIONS.PROCESSING,
    DISPOSITIONS.DISCARDED
];

// Dispositions that should NOT render a per-row chip — the column is left
// blank. Same idea as omitting from the summary strip: silence on the
// majority of rows lets the exceptions stand out.
export const SILENT_DISPOSITIONS = new Set([DISPOSITIONS.PROCESSED]);

/**
 * Pick the single most informative disposition for an event.
 *
 * Strict priority ordering — first match wins:
 *   1. Pipeline status (processing / failed) — overrides everything else.
 *   2. Non-NEW event type — duplicate / re-blast / re-surfaced / price
 *      change are themselves the actionable signal.
 *   3. Discarded — completed-but-junk (no address / nothing usable).
 *   4. Hot — LOOK_NOW + real ARV or profit. Same gate Home Top Deals applies.
 *   5. Processed — everything else that completed (Review/Later/Needs info/
 *      Archived/LOOK_NOW-without-numbers all collapse here per product call:
 *      only "Hot" earns its own visual; other tiers don't deserve eye candy.
 */
export function resolveDisposition(event) {
    const data = event?.lead?.data || {};
    const status = data.status;

    // Via isUnderwritingPending, not a raw status read: a resubmitted property keeps
    // status:'processing' forever because the merge path never lands a terminal status,
    // which parked its feed row on the "Processing" dot permanently instead of letting it
    // reach Hot / Processed. The outcome decides. See underwritingStatus.js.
    if (isUnderwritingPending(data)) return DISPOSITIONS.PROCESSING;
    if (status === 'failed') return DISPOSITIONS.DISCARDED;

    // Re-surfaced event always means the lead was just restored from archive
    // — surface that explicitly. (After this point isArchived is false, so
    // the ARCHIVED check below won't fire for these rows.)
    if (event?.type === EVENT_TYPES.RE_SURFACED) return DISPOSITIONS.RE_SURFACED;

    // Currently-archived lead — the user already dismissed this property,
    // so that's the headline regardless of original tier or any silent
    // duplicate events that didn't trigger auto-restore.
    if (data.isArchived === true) return DISPOSITIONS.ARCHIVED;

    if (event?.type === EVENT_TYPES.DUPLICATE)    return DISPOSITIONS.DUPLICATE;
    if (event?.type === EVENT_TYPES.RE_BLAST)     return DISPOSITIONS.RE_BLAST;
    if (event?.type === EVENT_TYPES.PRICE_CHANGE) return DISPOSITIONS.PRICE_CHANGE;

    // Completed leads that produced no usable signal are discarded, not
    // "processing" — the pipeline finished, it just had nothing to work with
    // (no address resolved, no tier scored). Most common cause: the inbound
    // raw text couldn't be parsed into a property.
    const hasAddress = !!data.address?.streetAddress;
    const tier = data.triage?.priority_tier;
    if (!hasAddress) return DISPOSITIONS.DISCARDED;
    if (!tier && !data.underwriting?.arv) return DISPOSITIONS.DISCARDED;

    if (tier === 'LOOK_NOW') {
        const arv = data.underwriting?.arv;
        const profit = data.underwriting?.estimatedProfit;
        const hasArv = arv != null && !isNaN(arv);
        const hasProfit = profit != null && !isNaN(profit);
        if (hasArv || hasProfit) return DISPOSITIONS.HOT;
    }

    return DISPOSITIONS.PROCESSED;
}
